import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { exchangeGoogleSession } from '../lib/api';

/**
 * AuthCallback — handles the Emergent OAuth redirect.
 *
 * Emergent returns the user to `${origin}/admin#session_id=<id>`. This
 * component is rendered by the top-level router BEFORE any protected route
 * gets to call /auth/me, so we can establish the session cookie cleanly.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Idempotency guard — StrictMode double-invokes effects in dev.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError('לא נמצא מזהה התחברות בקישור.');
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const user = await exchangeGoogleSession(sessionId);
        // Clean the URL fragment so a refresh doesn't re-trigger this flow.
        window.history.replaceState({}, '', window.location.pathname);
        navigate('/admin', { replace: true, state: { user } });
      } catch (err) {
        // Log the full error for diagnostics — the "friendly" text below hides details.
        console.error('[AuthCallback] Exchange failed', {
          status: err?.response?.status,
          detail: err?.response?.data?.detail,
          message: err?.message,
        });
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 403) {
          setError('הגישה נדחתה. הכתובת שלך אינה מורשית להיכנס למערכת.');
        } else if (status === 401) {
          setError('ההתחברות נכשלה — מזהה ההתחברות לא תקין או פג תוקף. נסו שוב.');
        } else if (status === 404 || status === 405) {
          setError('נקודת הקצה של ההתחברות לא נמצאה בשרת. ייתכן שנדרש Deploy חדש.');
        } else if (status === 502) {
          setError('לא ניתן היה להגיע לספק ההתחברות (Emergent OAuth). נסו שוב בעוד רגע.');
        } else if (status) {
          setError(`שגיאה בהתחברות (HTTP ${status}): ${detail || 'נסו שוב'}.`);
        } else {
          setError('שגיאת רשת: השרת לא זמין או שקיימת חסימת CORS. פרטים נוספים ב-console.');
        }
      }
    })();
  }, [navigate]);

  return (
    <section
      className="min-h-screen flex items-center justify-center bg-cream-200 px-6"
      aria-live="polite"
    >
      {error ? (
        <div className="max-w-md w-full bg-cream p-10 border border-navy/10 text-center" data-testid="auth-callback-error">
          <div className="w-12 h-12 mx-auto rounded-full bg-destructive/10 text-destructive flex items-center justify-center mb-5">
            <AlertCircle className="w-6 h-6" aria-hidden="true" />
          </div>
          <h1 className="display-2 text-navy mb-3">לא ניתן להיכנס</h1>
          <p className="text-navy/70 text-sm mb-7">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/admin', { replace: true })}
            className="inline-flex items-center gap-2 bg-navy text-cream px-6 py-3 text-sm hover:bg-navy-700 transition-colors"
            data-testid="auth-callback-back"
          >
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
            <span>חזרה למסך הכניסה</span>
          </button>
        </div>
      ) : (
        <div className="text-center" data-testid="auth-callback-loading">
          <Loader2 className="w-8 h-8 animate-spin text-navy mx-auto mb-4" aria-hidden="true" />
          <p className="text-navy/70 text-sm">מתחבר לחשבון Google…</p>
        </div>
      )}
    </section>
  );
};

export default AuthCallback;
